// src/pages/MarketingCollators.jsx
// Marketing creatives library (Drive folders via Apps Script) with
// share-to-WhatsApp to the dealer's customers. Adapted for SOTA:
//  - customers come from the shared DB (/api/customers)
//  - WhatsApp send goes through the SOTA backend (/api/send-creative)
//  - dealer GST read from the SOTA dealer object (best-effort, for logging)

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../utils/api";
import { sendWhatsAppImage, getMetaBulkStatus, sendBulkViaMeta } from "../utils/whatsapp";

/* =========================
   CONFIG
========================= */
const API =
  "https://script.google.com/macros/s/AKfycbwxJPdc6sG0HbzR8_tXR3GuFtwN09pc1lL5EDbMHNIqKwW-MQp_nK6BC1KvsEKMrtOsQg/exec";

const SOURCES = [
  { label: "CREATIVES - MAIN", folderId: "1drrZO09wn-bWDLDBbyZ5u-E6b-tphh_G" },
  { label: "CREATIVES - AUDIO", folderId: "1GhDq4y44CIHuq5O-2OroPqmieDdwiwjE" },
  { label: "BROCHURES - PDF", folderId: "1SR_fS436P1PJHK0vCPRi8w3atVN7oE9y" }, // "Marketing collatelars" (public)
  { label: "BROCHURES - IMAGE", folderId: "1ErwqVA20b8gJxSTTaQfWUcBPX5OBs2-b" },
];

const CACHE_KEY = "cache:marketing_collators:v4"; // v4: PDFs moved to the public folder

/* =========================
   Dealer helpers (SOTA)
========================= */
function getDealerPhone() {
  const direct = String(localStorage.getItem("loginPhone") || "").replace(/\D/g, "");
  if (direct.length >= 10) return direct.slice(-10);
  try {
    const d = JSON.parse(localStorage.getItem("dealer") || "{}");
    const p = String(d.phone || d["Contact No"] || "").replace(/\D/g, "");
    if (p.length >= 10) return p.slice(-10);
  } catch {}
  return "";
}
function getLoggedInDealerGST() {
  try {
    const d = JSON.parse(localStorage.getItem("dealer") || "{}");
    return String(d.gst || d["GSTIN/UIN"] || d.GSTIN || "").trim();
  } catch {
    return "";
  }
}

/* =========================
   Google Form (silent log)
========================= */
const FORM_RESPONSE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfv4PmVKDNAZaNHQVF7YH81GvpZIFPgaGiwBDcCKn-UktzYpw/formResponse";

const FORM_ENTRIES = {
  dealerGst: "entry.316243280",
  image: "entry.839319200",
  customerId: "entry.1126012646",
  customerMobile: "entry.1967954327",
  status: "entry.446780867",
};

async function submitShareLogToGoogleForm({
  dealerGst,
  imageUrl,
  customerId,
  customerMobile,
  status,
}) {
  try {
    const fd = new URLSearchParams();
    fd.append(FORM_ENTRIES.dealerGst, dealerGst || "");
    fd.append(FORM_ENTRIES.image, imageUrl || "");
    fd.append(FORM_ENTRIES.customerId, customerId || "");
    fd.append(FORM_ENTRIES.customerMobile, customerMobile || "");
    fd.append(FORM_ENTRIES.status, status || "");
    await fetch(FORM_RESPONSE_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: fd.toString(),
    });
    return true;
  } catch (e) {
    console.error("Form submit failed:", e);
    return false;
  }
}

/* =========================
   Toast (no library)
========================= */
function useToasts() {
  const [toasts, setToasts] = useState([]);

  const toast = (message, type = "info", duration = 2600) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, message, type }]);
    window.__toastTimers = window.__toastTimers || {};
    window.clearTimeout(window.__toastTimers[id]);
    window.__toastTimers[id] = window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  function ToastViewport() {
    return (
      <div className="fixed top-3 right-3 z-[9999] flex flex-col gap-2 w-[92vw] sm:w-[380px]">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : t.type === "error"
                ? "border-rose-200 bg-rose-50 text-rose-900"
                : t.type === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-gray-200 bg-white text-gray-900";
          const dot =
            t.type === "success"
              ? "bg-emerald-500"
              : t.type === "error"
                ? "bg-rose-500"
                : t.type === "warning"
                  ? "bg-amber-500"
                  : "bg-blue-500";
          return (
            <div key={t.id} className={`rounded-2xl border shadow-sm px-4 py-3 flex items-start gap-3 ${styles}`}>
              <span className={`mt-1 h-2 w-2 rounded-full ${dot}`} />
              <div className="flex-1 text-sm font-semibold leading-snug">{t.message}</div>
              <button
                onClick={() => removeToast(t.id)}
                className="text-xs font-black opacity-60 hover:opacity-100"
                aria-label="Close"
                type="button"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  return { toast, ToastViewport };
}

/* =========================
   Small helpers
========================= */
function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}
function safeGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}
function normalizeGst(v) {
  return String(v || "").toUpperCase().replace(/\s+/g, "").trim();
}
function clean10(v) {
  return String(v || "").replace(/\D/g, "").slice(-10);
}

/* =========================
   JSONP helper (fallback)
========================= */
function jsonp(url, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    const cb = "cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    const sep = url.includes("?") ? "&" : "?";
    const fullUrl = `${url}${sep}callback=${cb}`;
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("JSONP timeout"));
    }, timeoutMs);
    function cleanup() {
      clearTimeout(timer);
      if (window[cb]) delete window[cb];
      if (script.parentNode) script.parentNode.removeChild(script);
    }
    window[cb] = (data) => {
      cleanup();
      resolve(data);
    };
    script.onerror = () => {
      cleanup();
      reject(new Error("JSONP failed"));
    };
    script.src = fullUrl;
    document.body.appendChild(script);
  });
}

/* =========================
   IFRAME fetch (postMessage bridge)
========================= */
function iframeFetchJson({ url, timeoutMs = 20000 }) {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.left = "-9999px";
    frame.style.top = "-9999px";
    frame.width = "1";
    frame.height = "1";
    const token = "t_" + Math.random().toString(36).slice(2);
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("Iframe fetch timeout"));
    }, timeoutMs);
    const onMsg = (ev) => {
      const data = ev?.data;
      if (!data || data.__token !== token) return;
      cleanup();
      resolve(data.payload);
    };
    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener("message", onMsg);
      if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
    }
    window.addEventListener("message", onMsg);
    frame.src = `${url}${url.includes("?") ? "&" : "?"}__token=${encodeURIComponent(token)}`;
    document.body.appendChild(frame);
  });
}

async function fetchFolderSmart(url) {
  // The Apps Script answers JSONP — go straight to it. The iframe bridge never
  // gets a postMessage reply from this script, so trying it first only added
  // an 8s wait per folder; it stays as a last resort.
  try {
    return await jsonp(url, 20000);
  } catch {
    return await iframeFetchJson({ url, timeoutMs: 8000 });
  }
}

/* =========================
   MIME helpers
========================= */
function isImage(mime) {
  return String(mime || "").startsWith("image/");
}
function isAudio(mime) {
  return String(mime || "").startsWith("audio/");
}
function isVideo(mime) {
  return String(mime || "").startsWith("video/");
}
function isPdf(mime) {
  return String(mime || "") === "application/pdf";
}
function getTypeLabel(mime) {
  if (isImage(mime)) return "Image";
  if (isVideo(mime)) return "Video";
  if (isAudio(mime)) return "Audio";
  if (isPdf(mime)) return "PDF";
  return "Other";
}
// Build a reliable, SMALL Drive thumbnail. drive.google.com/thumbnail?id=..
// loads without auth and we keep it small (w512) for speed. Falls back to the
// raw thumbUrl/viewUrl if no usable file id.
function cardThumb(file) {
  const id = file?.fileId || file?.id || "";
  if (/^[a-zA-Z0-9_-]{20,}$/.test(String(id)))
    return `https://drive.google.com/thumbnail?id=${id}&sz=w512`;
  return file?.thumbUrl || file?.viewUrl || "";
}
function getTypePillClasses(mime) {
  if (isImage(mime)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (isVideo(mime)) return "bg-violet-50 text-violet-700 border-violet-200";
  if (isAudio(mime)) return "bg-amber-50 text-amber-800 border-amber-200";
  if (isPdf(mime)) return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

/* =========================
   Cache marketing
========================= */
function loadMarketingCache() {
  const cached = safeGet(CACHE_KEY, null);
  if (!cached) return { data: [], ts: 0 };
  return {
    data: Array.isArray(cached?.data) ? cached.data : [],
    ts: Number(cached?.ts || 0),
  };
}
function saveMarketingCache(data) {
  safeSet(CACHE_KEY, { data, ts: Date.now() });
}
function sortFiles(files) {
  return [...files].sort(
    (a, b) =>
      String(a.source || "").localeCompare(String(b.source || "")) ||
      String(a.name || "").localeCompare(String(b.name || ""))
  );
}

/* =========================
   Caption
========================= */
function buildCaption({ file }) {
  const type = getTypeLabel(file?.mime);
  const name = String(file?.name || "Creative");
  return `Friends Steel — ${type}\n${name}\n\nPlease check and share your feedback.`;
}

/* =========================
   MAIN
========================= */
export default function MarketingCollators() {
  const navigate = useNavigate();
  const { toast, ToastViewport } = useToasts();
  const dealerGst = useMemo(() => normalizeGst(getLoggedInDealerGST()), []);

  const cached0 = useMemo(() => loadMarketingCache(), []);
  const [rows, setRows] = useState(cached0.data);

  const [loading, setLoading] = useState(rows.length === 0);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState("");
  const [sourceErrors, setSourceErrors] = useState({});

  // filters
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");

  // share modal
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFile, setShareFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  // customers
  const [customers, setCustomers] = useState([]);
  const [custQ, setCustQ] = useState("");
  const [selected, setSelected] = useState({});
  const [sendSelf, setSendSelf] = useState(false); // also send to the dealer
  const [sending, setSending] = useState(false);
  // Bulk to customers is ON only when the dealer's Meta WhatsApp number is set up.
  const [metaBulk, setMetaBulk] = useState({ enabled: false, metaPhone: "" });

  const dealerSelf = useMemo(() => getDealerPhone(), []);

  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [sendLog, setSendLog] = useState([]);

  const fetchingRef = useRef(false);

  const closeShare = () => {
    setShareOpen(false);
    setShareFile(null);
    setCustQ("");
    setSelected({});
    setSendSelf(false);
    setSending(false);
    setProgress({ done: 0, total: 0 });
    setSendLog([]);
  };

  // customers from the shared DB (/api/customers)
  useEffect(() => {
    let alive = true;
    (async () => {
      const phone = getDealerPhone();
      if (!phone) return;
      getMetaBulkStatus().then((s) => alive && setMetaBulk({ ...s, loaded: true }));
      try {
        const res = await fetch(
          `${API_BASE}/api/customers?phone=${encodeURIComponent(phone)}`,
          { headers: { "x-auth-phone": phone } }
        );
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok && data.success) {
          setCustomers(
            (data.customers || []).map((c) => ({
              id: c._id,
              customerId: String(c._id || ""),
              name: String(c.name || ""),
              phone: clean10(c.mobile),
              type: String(c.type || ""),
              area: String(c.area || ""),
              district: String(c.district || ""),
            }))
          );
        }
      } catch {
        /* keep empty */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // quiet=true → silent background refresh (no spinner/progress), used when
  // cached data is already on screen so it never "loads again" visibly.
  // fromSource=true skips the backend cache and asks Apps Script directly (Refresh Now).
  async function loadAll({ quiet = false, fromSource = false } = {}) {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!quiet) {
      setLoading(true);
      setError("");
      setSourceErrors({});
    }
    try {
      // Fast path: the backend keeps these folder listings cached (<1s).
      if (!fromSource) {
        try {
          const r = await fetch(`${API_BASE}/api/sheets/marketing`, { cache: "no-store" });
          const j = await r.json().catch(() => ({}));
          if (r.ok && Array.isArray(j.data) && j.data.length > 0) {
            const files = sortFiles(j.data);
            setRows(files);
            saveMarketingCache(files);
            return;
          }
        } catch {
          /* fall back to Apps Script below */
        }
      }

      const all = [];
      const errs = {};
      await Promise.all(
        SOURCES.map(async (src) => {
          try {
            if (!quiet) setProgressText(`Loading: ${src.label}`);
            const url = `${API}?folderId=${encodeURIComponent(src.folderId)}`;
            const res = await fetchFolderSmart(url);
            const data = Array.isArray(res) ? res : res?.data;
            if (Array.isArray(data)) {
              data.forEach((f) => all.push({ ...f, source: src.label }));
            } else {
              errs[src.label] = "Invalid response (no data array)";
            }
          } catch (e) {
            errs[src.label] = e?.message || "Failed";
          }
        })
      );
      all.sort((a, b) => {
        const s1 = String(a.source || "").localeCompare(String(b.source || ""));
        if (s1 !== 0) return s1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      // Only replace rows if we actually got data (don't blank the screen on
      // a failed background refresh).
      if (all.length > 0 || !quiet) {
        setRows(all);
        saveMarketingCache(all);
      }
      if (!quiet) setSourceErrors(errs);
      if (!quiet && all.length === 0) {
        const anyErr = Object.values(errs).filter(Boolean).length > 0;
        if (anyErr) setError("No files loaded. The marketing source may be private or unreachable.");
      }
    } catch (e) {
      if (!quiet) setError(e?.message || "Failed to load files");
    } finally {
      if (!quiet) {
        setLoading(false);
        setProgressText("");
      }
      fetchingRef.current = false;
    }
  }

  // Initial: show cached instantly, then refresh in the BACKGROUND (quiet) if
  // we already have cache; otherwise show the loader once.
  useEffect(() => {
    loadAll({ quiet: rows.length > 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshNow = () => loadAll({ quiet: false, fromSource: true });

  const sourcesAvailable = useMemo(() => {
    const s = new Set(rows.map((r) => String(r.source || "").trim()).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((f) => {
      const type = getTypeLabel(f.mime);
      const okType = typeFilter === "ALL" ? true : type === typeFilter;
      const okSource = sourceFilter === "ALL" ? true : String(f.source || "") === sourceFilter;
      const okSearch = !s
        ? true
        : String(f.name || "").toLowerCase().includes(s) ||
          String(f.source || "").toLowerCase().includes(s) ||
          String(type || "").toLowerCase().includes(s);
      return okType && okSource && okSearch;
    });
  }, [rows, q, typeFilter, sourceFilter]);

  const filteredCustomers = useMemo(() => {
    const s = custQ.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => {
      const name = String(c.name || "").toLowerCase();
      const phone = String(c.phone || "").toLowerCase();
      const area = String(c.area || "").toLowerCase();
      const type = String(c.type || "").toLowerCase();
      const custId = String(c.customerId || "").toLowerCase();
      return name.includes(s) || phone.includes(s) || area.includes(s) || type.includes(s) || custId.includes(s);
    });
  }, [customers, custQ]);

  const selectedPhones = useMemo(() => Object.keys(selected).filter((p) => selected[p]), [selected]);

  // "Myself" always; selected customers only when the dealer's Meta WhatsApp
  // Business number is set up (otherwise bulk is behind the "Contact us" request).
  const targetPhones = useMemo(() => {
    const self = sendSelf && dealerSelf ? [dealerSelf] : [];
    if (!metaBulk.enabled) return self;
    return [...self, ...selectedPhones.filter((p) => p && p !== dealerSelf)];
  }, [sendSelf, dealerSelf, metaBulk.enabled, selectedPhones]);

  const allChecked = useMemo(() => {
    if (filteredCustomers.length === 0) return false;
    return filteredCustomers.every((c) => selected[String(c.phone || "")] === true);
  }, [filteredCustomers, selected]);

  const toggleAll = () => {
    const next = { ...selected };
    const shouldCheck = !allChecked;
    filteredCustomers.forEach((c) => {
      const p = String(c.phone || "");
      if (p) next[p] = shouldCheck;
    });
    setSelected(next);
  };

  const toggleOne = (phone) => {
    setSelected((prev) => ({ ...prev, [phone]: !prev[phone] }));
  };

  const onShareClick = (file) => {
    setShareFile(file);
    setSendSelf(false);
    setShareOpen(true);
  };

  const shareViaWhatsAppAuto = async () => {
    if (!shareFile) return;
    if (targetPhones.length === 0) {
      toast("Please select at least 1 customer (or 'myself').", "warning", 2600);
      return;
    }

    const caption = ""; // send file only — no caption/footer text
    // For non-images (PDF/audio/video) the download URL is the real file.
    const imageUrl = shareFile.downloadUrl || shareFile.viewUrl || shareFile.thumbUrl || "";
    if (!/^https?:\/\//i.test(String(imageUrl || ""))) {
      toast("File URL is not a public http/https URL.", "error", 3200);
      return;
    }
    const fileName = String(shareFile.name || "creative");

    setSending(true);
    setProgress({ done: 0, total: targetPhones.length });
    setSendLog([]);
    toast(`Sharing started… (${targetPhones.length} recipients)`, "info", 2200);

    try {
      let okCount = 0;
      let failCount = 0;
      const record = async (phone, ok, error) => {
        const cust = customers.find((c) => String(c.phone || "") === phone);
        const name = cust?.name || (phone === dealerSelf ? "You" : "");
        const status = ok ? "SENT" : "FAILED";

        await submitShareLogToGoogleForm({
          dealerGst,
          imageUrl,
          customerId: cust?.customerId || "",
          customerMobile: phone,
          status,
        });

        setSendLog((prev) => [
          ...prev,
          { phone, name, ok, status, error: ok ? "" : String(error || "Failed") },
        ]);

        if (ok) okCount++;
        else {
          failCount++;
          toast(`Failed: ${name || phone}`, "error", 2400);
        }

        setProgress((p) => ({ ...p, done: Math.min(p.total, p.done + 1) }));
      };

      // Myself → Maytapi (existing path).
      if (dealerSelf && targetPhones.includes(dealerSelf)) {
        const res = await sendWhatsAppImage({
          phone: dealerSelf,
          imageUrl,
          caption,
          filename: fileName,
        });
        await record(dealerSelf, !!res?.success, res?.error);
      }

      // Customers → Meta Cloud API from the dealer's own number (upload once, chunked send).
      const custPhones = targetPhones.filter((p) => p !== dealerSelf);
      if (custPhones.length) {
        await sendBulkViaMeta({
          phones: custPhones,
          imageUrl,
          filename: fileName,
          caption,
          onResult: (r) => record(r.phone, !!r.ok, r.error),
        });
      }

      if (failCount === 0) toast(`✅ Shared successfully to ${okCount}`, "success", 3200);
      else toast(`Done: ✅ ${okCount} | ❌ ${failCount}`, "warning", 3600);
      closeShare();
    } catch (e) {
      console.error(e);
      toast("❌ Auto share failed.", "error", 3200);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!shareOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [shareOpen]);

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 pt-16 md:pt-24 pb-28 md:pb-10">
      <ToastViewport />

      <div className="w-full px-3 sm:px-4 lg:px-8">
        {/* Header — simple */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="h-9 w-9 rounded-full border bg-white font-bold hover:bg-gray-50 shrink-0"
              title="Back to dashboard"
            >
              ←
            </button>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
              Marketing Collaterals
            </h1>
          </div>
          <button
            type="button"
            onClick={refreshNow}
            className="px-3 py-2 rounded-xl border font-semibold hover:bg-gray-50 text-sm"
          >
            Refresh Now
          </button>
        </div>

        {(loading || progressText) && (
          <div className="mb-4">
            <TopLoader text={progressText || "Loading files…"} />
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-2xl border bg-rose-50 text-rose-700 p-3 text-sm">
            <div className="font-bold">Error</div>
            <div className="mt-1">{error}</div>
            {Object.keys(sourceErrors || {}).length > 0 && (
              <div className="mt-3 text-xs">
                <div className="font-semibold">Source diagnostics:</div>
                <ul className="mt-1 list-disc pl-4">
                  {Object.entries(sourceErrors).map(([k, v]) => (
                    <li key={k}>
                      <span className="font-semibold">{k}:</span> <span className="opacity-90">{v || "OK"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* FILTER BAR */}
        <div className="mt-4 sticky top-2 z-20">
          <div className="rounded-[24px] border bg-white/80 backdrop-blur shadow-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search file name / type / source…"
                  className="w-full px-3 py-2 rounded-2xl border bg-white focus:outline-none focus:ring-2 focus:ring-black/20"
                />
              </div>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 rounded-2xl border bg-white text-sm font-semibold"
              >
                {sourcesAvailable.map((s) => (
                  <option key={s} value={s}>
                    {s === "ALL" ? "All Sources" : s}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-500 ml-auto">
                Showing <span className="font-semibold text-gray-900">{filteredRows.length}</span> / {rows.length}
              </div>
            </div>
          </div>
        </div>

        {/* GRID */}
        <div className="mt-4">
          {loading && rows.length === 0 ? (
            <GridSkeleton />
          ) : filteredRows.length === 0 ? (
            <EmptyState onRefresh={refreshNow} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {filteredRows.map((f) => (
                <FileCard key={f.id || `${f.source}-${f.name}`} file={f} onShare={onShareClick} onPreview={setPreviewFile} />
              ))}
            </div>
          )}
        </div>

        <div className="h-8" />

        {/* PREVIEW MODAL */}
        {previewFile && (
          <PreviewModal
            file={previewFile}
            onClose={() => setPreviewFile(null)}
            onShare={(f) => {
              setPreviewFile(null);
              onShareClick(f);
            }}
          />
        )}

        {/* SHARE MODAL */}
        {shareOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={sending ? undefined : closeShare} />
            <div className="absolute inset-0 flex items-center justify-center px-3 py-4 sm:py-6">
              <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border overflow-hidden max-h-[92vh] flex flex-col">
                <div className="p-5 border-b flex items-start justify-between gap-3 shrink-0">
                  <div>
                    <div className="text-xs text-gray-500">Share Creative</div>
                    <div className="text-lg font-extrabold text-gray-900">{shareFile?.name || "—"}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      Dealer GST: <span className="font-semibold text-gray-900">{dealerGst || "—"}</span>
                    </div>
                  </div>
                  <button
                    onClick={sending ? undefined : closeShare}
                    className="h-10 w-10 rounded-2xl border font-bold hover:bg-gray-50 disabled:opacity-60"
                    type="button"
                    aria-label="Close"
                    disabled={sending}
                  >
                    ✕
                  </button>
                </div>

                <div className="p-5 flex-1 overflow-y-auto">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-[240px]">
                      {isImage(shareFile?.mime) ? (
                        <img src={shareFile?.thumbUrl} alt={shareFile?.name} className="w-56 h-32 object-cover rounded-2xl border" />
                      ) : (
                        <div className="w-56 h-32 rounded-2xl border bg-gray-50 flex items-center justify-center text-sm text-gray-600">
                          {getTypeLabel(shareFile?.mime)}
                        </div>
                      )}
                      <div className="mt-2">
                        <span className={cx("inline-flex px-2.5 py-1 rounded-full text-xs font-bold border", getTypePillClasses(shareFile?.mime))}>
                          {getTypeLabel(shareFile?.mime)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">{shareFile?.source || ""}</span>
                      </div>
                    </div>

                    {metaBulk.enabled && (
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-extrabold text-gray-900">Select Customers</span>
                        <span className="text-xs text-gray-500">
                          ({filteredCustomers.length} shown, {selectedPhones.length} selected)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          value={custQ}
                          onChange={(e) => setCustQ(e.target.value)}
                          placeholder="Search name / mobile / area / type"
                          className="w-full sm:w-[380px] px-3 py-2 rounded-2xl border bg-white focus:outline-none focus:ring-2 focus:ring-black/20"
                        />
                        <button type="button" onClick={toggleAll} className="px-3 py-2 rounded-2xl border font-semibold hover:bg-gray-50">
                          {allChecked ? "Unselect All" : "Select All"}
                        </button>
                      </div>
                    </div>
                    )}
                  </div>

                  {/* Send to myself */}
                  {dealerSelf && (
                    <label
                      className={cx(
                        "mt-4 flex items-center gap-3 px-3 py-2 rounded-2xl border cursor-pointer transition",
                        sendSelf ? "border-green-400 bg-green-50" : "border-gray-200 hover:bg-gray-50"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={sendSelf}
                        onChange={() => setSendSelf((s) => !s)}
                        className="h-4 w-4"
                      />
                      <span className="h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0">
                        📱
                      </span>
                      <span className="text-sm">
                        <span className="font-bold text-gray-900">Send to myself</span>{" "}
                        <span className="text-gray-500">(+91 {dealerSelf})</span>
                      </span>
                    </label>
                  )}

                  {/* Not connected yet → how to connect their own WhatsApp Business number */}
                  {metaBulk.loaded && !metaBulk.enabled && (
                    <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-3">
                      <p className="text-sm font-bold text-blue-900">
                        📲 Send to your customers from your own WhatsApp
                      </p>
                      <p className="text-xs text-blue-800 mt-0.5">
                        Connect your WhatsApp Business number once (about 10 minutes). After that you can share creatives with all your customers from here.
                      </p>
                      <a
                        href="/whatsapp-connect-steps.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 flex w-full justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition text-sm"
                      >
                        View connection steps (PDF)
                      </a>
                    </div>
                  )}

                  {/* Customers — only when the dealer's WhatsApp Business number is active */}
                  {metaBulk.enabled && (
                  <>
                  <div className="mt-4 rounded-2xl border border-green-300 bg-green-50 p-3">
                    <p className="text-sm font-bold text-green-900">
                      📦 Bulk sharing ON
                    </p>
                    <p className="text-xs text-green-800 mt-0.5">
                      Selected customers will receive it from your WhatsApp Business number
                      {metaBulk.metaPhone ? ` +91 ${metaBulk.metaPhone}` : ""}.
                    </p>
                  </div>

                  <div className="mt-4 border rounded-3xl overflow-hidden">
                    <div className="max-h-[320px] overflow-auto">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-5 text-sm text-gray-500">
                          No customers found. Add customers in Vyapar Sales first.
                        </div>
                      ) : (
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr className="text-left">
                              <th className="px-4 py-3 w-[52px]">✓</th>
                              <th className="px-4 py-3 font-bold text-gray-700">Name</th>
                              <th className="px-4 py-3 font-bold text-gray-700">Mobile</th>
                              <th className="px-4 py-3 font-bold text-gray-700">Area</th>
                              <th className="px-4 py-3 font-bold text-gray-700">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCustomers.map((c) => {
                              const p = String(c.phone || "");
                              const checked = !!selected[p];
                              return (
                                <tr key={c.customerId || c.id || p} className="border-t hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <input type="checkbox" checked={checked} onChange={() => toggleOne(p)} disabled={!p} className="h-4 w-4" />
                                  </td>
                                  <td className="px-4 py-3 font-semibold text-gray-900 uppercase">{c.name || "—"}</td>
                                  <td className="px-4 py-3 text-gray-700">{p || "—"}</td>
                                  <td className="px-4 py-3 text-gray-700">{c.area || "—"}</td>
                                  <td className="px-4 py-3">
                                    <span className="inline-flex px-2 py-1 rounded-full text-xs border bg-white">{c.type || "—"}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                  </>
                  )}
                  <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
                    <button type="button" onClick={closeShare} disabled={sending} className="px-4 py-2 rounded-2xl border font-semibold hover:bg-gray-50 disabled:opacity-60">
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={sending || targetPhones.length === 0}
                      onClick={shareViaWhatsAppAuto}
                      className="px-4 py-2 rounded-2xl bg-green-600 text-white font-extrabold hover:opacity-90 disabled:opacity-60"
                    >
                      {sending
                        ? `Sending ${progress.done}/${progress.total}`
                        : metaBulk.enabled
                          ? `Share to WhatsApp (${targetPhones.length})`
                          : "Share to WhatsApp (myself)"}
                    </button>
                  </div>

                  {progress.total > 0 && (
                    <div className="mt-3 text-xs text-gray-600">
                      Progress: <b>{progress.done}</b> / <b>{progress.total}</b>
                    </div>
                  )}

                  {sendLog.length > 0 && (
                    <div className="mt-3 border rounded-2xl overflow-hidden">
                      <div className="max-h-[180px] overflow-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr className="text-left">
                              <th className="px-3 py-2">Customer</th>
                              <th className="px-3 py-2">Phone</th>
                              <th className="px-3 py-2">Status</th>
                              <th className="px-3 py-2">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendLog.map((x, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-3 py-2 font-semibold">{x.name || "—"}</td>
                                <td className="px-3 py-2">{x.phone}</td>
                                <td className="px-3 py-2">
                                  {x.ok ? <span className="text-emerald-700 font-bold">SENT</span> : <span className="text-rose-700 font-bold">FAILED</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{x.error || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================
   UI Components
====================== */
function TopLoader({ text }) {
  return (
    <div className="rounded-3xl border bg-white shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-gray-900/10 animate-pulse" />
        <div className="flex-1">
          <div className="text-sm font-extrabold text-gray-900">Loading</div>
          <div className="mt-1 text-xs text-gray-600">{text || "Please wait…"}</div>
          <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full w-2/3 bg-gray-900/20 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="rounded-[24px] border bg-white shadow-sm overflow-hidden animate-pulse">
          <div className="h-40 bg-gray-200" />
          <div className="p-4">
            <div className="h-4 w-2/3 bg-gray-200 rounded" />
            <div className="mt-2 h-3 w-1/2 bg-gray-200 rounded" />
            <div className="mt-4 flex gap-2">
              <div className="h-9 w-20 bg-gray-200 rounded-2xl" />
              <div className="h-9 w-24 bg-gray-200 rounded-2xl" />
              <div className="h-9 w-20 bg-gray-200 rounded-2xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onRefresh }) {
  return (
    <div className="rounded-[28px] border bg-white shadow-sm p-6 text-center">
      <div className="text-lg font-black text-gray-900">No files found</div>
      <div className="mt-1 text-sm text-gray-600">Try changing filters or refresh.</div>
      <div className="mt-4">
        <button type="button" onClick={onRefresh} className="px-4 py-2 rounded-2xl bg-black text-white font-extrabold hover:opacity-90">
          Refresh Now
        </button>
      </div>
    </div>
  );
}

function driveFileId(file) {
  const id = String(file?.fileId || file?.id || "");
  return /^[a-zA-Z0-9_-]{20,}$/.test(id) ? id : "";
}

// Full-size in-app preview: images as <img>, PDFs/videos via Drive's /preview player.
function PreviewModal({ file, onClose, onShare }) {
  const id = driveFileId(file);
  const mime = file?.mime || "";
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeRef.current();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-5xl h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b">
          <span className={cx("inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border whitespace-nowrap", getTypePillClasses(mime))}>
            {getTypeLabel(mime)}
          </span>
          <div className="flex-1 min-w-0 text-sm font-extrabold text-gray-900 truncate" title={file.name}>
            {file.name}
          </div>
          <a href={file.downloadUrl} target="_blank" rel="noreferrer" className="hidden sm:inline-flex px-3 py-2 rounded-2xl bg-gray-900 text-white hover:opacity-90 text-sm font-extrabold">
            Download
          </a>
          <button onClick={() => onShare(file)} className="px-3 py-2 rounded-2xl bg-green-600 text-white hover:opacity-90 text-sm font-extrabold" type="button">
            Share
          </button>
          <button onClick={onClose} className="h-9 w-9 rounded-2xl border font-bold hover:bg-gray-50" type="button" aria-label="Close preview">
            ✕
          </button>
        </div>
        <div className="flex-1 bg-gray-100">
          {isImage(mime) ? (
            <img
              src={id ? `https://lh3.googleusercontent.com/d/${id}=w1600` : file.thumbUrl}
              alt={file.name}
              className="w-full h-full object-contain"
              referrerPolicy="no-referrer"
            />
          ) : id ? (
            <iframe title={file.name} src={`https://drive.google.com/file/d/${id}/preview`} className="w-full h-full border-0" allow="autoplay" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-gray-600">Preview not available</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileCard({ file, onShare, onPreview }) {
  const mime = file?.mime || "";
  const type = getTypeLabel(mime);
  const hasThumb = isImage(mime) || isPdf(mime) || isVideo(mime);
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumb = hasThumb && !thumbFailed;

  // A failed Drive thumbnail means the file isn't public — the in-app player
  // would fail too, so open it in Drive instead.
  const openPreview = () => {
    if (showThumb) onPreview(file);
    else window.open(file.viewUrl, "_blank", "noopener");
  };

  return (
    <div className="rounded-[24px] border bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <button
        type="button"
        onClick={openPreview}
        className="relative block w-full h-44 bg-gray-50 overflow-hidden cursor-zoom-in"
        title={`Preview ${file.name || ""}`}
      >
        {showThumb ? (
          <img
            src={cardThumb(file)}
            alt={file.name}
            className={cx("w-full h-full object-cover", !isImage(mime) && "object-top")}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              const fb = isImage(mime) ? file.thumbUrl || "" : "";
              if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
              else setThumbFailed(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm text-gray-600">{type}</div>
        )}
        {showThumb && !isImage(mime) && (
          <span className={cx("absolute left-3 top-3 inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border shadow-sm", getTypePillClasses(mime))}>
            {type}
          </span>
        )}
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-gray-900 truncate" title={file.name}>
              {file.name || "—"}
            </div>
            <div className="mt-1 text-xs text-gray-500 truncate" title={file.source}>
              {file.source || "—"}
            </div>
          </div>
          <span className={cx("inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border whitespace-nowrap", getTypePillClasses(mime))} title={mime}>
            {type}
          </span>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <a href={file.viewUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-2xl border text-gray-800 hover:bg-gray-50 text-sm font-semibold">
            Open
          </a>
          <a href={file.downloadUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-2xl bg-gray-900 text-white hover:opacity-90 text-sm font-extrabold">
            Download
          </a>
          <button onClick={() => onShare(file)} className="px-3 py-2 rounded-2xl bg-green-600 text-white hover:opacity-90 text-sm font-extrabold" type="button">
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
